import { useCallback, useEffect, useRef, useState } from "react";
import { paymentsService, DailyByOperatorStats } from "../../services/paymentsService";

interface UsePaymentStatsByOperatorParams {
  dateFrom?: string;
  dateTo?: string;
  senderCountryId?: number;
  receiverCountryId?: number;
  onlySucceeded?: boolean;
  /** Si false, la requête n'est pas déclenchée automatiquement */
  enabled?: boolean;
}

interface UsePaymentStatsByOperatorResult {
  data: DailyByOperatorStats[];
  loading: boolean;
  error: string | null;
  refetch: (overrides?: UsePaymentStatsByOperatorParams) => Promise<void>;
}

export default function usePaymentStatsByOperator(
  params?: UsePaymentStatsByOperatorParams
): UsePaymentStatsByOperatorResult {
  const [data, setData] = useState<DailyByOperatorStats[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Évite les mises à jour d'état après démontage
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchStats = useCallback(
    async (overrides?: UsePaymentStatsByOperatorParams) => {
      const token = localStorage.getItem("token"); // adaptez à votre source de token
      if (!token) {
        setError("Token manquant");
        return;
      }

      const merged = { ...params, ...overrides };

      setLoading(true);
      setError(null);
      try {
        const result = await paymentsService.getDailyStatsByOperator(token, {
          dateFrom: merged.dateFrom,
          dateTo: merged.dateTo,
          senderCountryId: merged.senderCountryId,
          receiverCountryId: merged.receiverCountryId,
          onlySucceeded: merged.onlySucceeded,
        });
        if (isMountedRef.current) {
          setData(result ?? []);
        }
      } catch (err: any) {
        if (isMountedRef.current) {
          setError(err?.response?.data?.message || err?.message || "Erreur lors du chargement des statistiques");
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params?.dateFrom, params?.dateTo, params?.senderCountryId, params?.receiverCountryId, params?.onlySucceeded]
  );

  useEffect(() => {
    if (params?.enabled === false) return;
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStats]);

  return { data, loading, error, refetch: fetchStats };
}
